<script setup lang="ts">
import { computed, onMounted, provide, ref, useSlots, watch, type VNode } from "vue";

import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import { useRoute, useRouter } from "vue-router";
import ColorBadge from "./ColorBadge.vue";

const { usesAnchor = true } = defineProps<{ usesAnchor?: boolean }>();
const emit = defineEmits<{
  tabChange: [tabName: string];
}>();

const slots = useSlots();
const selectedTabName = ref("");
const router = useRouter();
const route = useRoute();
provide("selectedTabName", selectedTabName);
defineExpose({ selectTab });

const tabs = computed(() => {
  const children = slots.default?.({}) || [];
  return children
    .filter((child: VNode) => child.type === TabPanelContent)
    .map((child: VNode) => ({
      name: child.props?.name,
      icon: child.props?.icon,
      disabled: child.props?.disabled,
      badge: child.props?.badge,
      badgeLabel: child.props?.badgeLabel,
    }));
});

function selectTab(tabName: string) {
  emit("tabChange", tabName);
  selectedTabName.value = tabName;
  if (usesAnchor) {
    const path = `${route.path}#${tabName}`;
    router.replace(path);
  }
}

function selectTabByRoute() {
  const selected =
    route.hash && usesAnchor ? route.hash.replace("#", "") : tabs.value[0].name || "";
  selectTab(selected);
}

watch(
  () => route.hash,
  () => {
    selectTabByRoute();
  }
);

onMounted(() => {
  selectTabByRoute();
});
</script>

<template>
  <div class="tab-panel">
    <div class="tab-selector">
      <div
        v-for="({ name, icon, disabled, badge, badgeLabel }, i) in tabs"
        :key="i"
        @click="!disabled && selectTab(name)"
        class="tab-selector-element"
        :class="{ selected: name === selectedTabName, disabled }"
      >
        <i v-if="icon" class="icon" :class="icon" />
        <span class="name">
          {{ name }}
        </span>
        <ColorBadge
          v-if="badgeLabel"
          :color="badge.color"
          :left-icon="badge.leftIcon"
          :right-icon="badge.rightIcon"
          size="s"
        >
          {{ badgeLabel }}
        </ColorBadge>
      </div>
    </div>
    <div class="tabs">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.tab-panel {
  .tab-selector {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    border-bottom: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    gap: var(--spacing-6);

    & .tab-selector-element {
      padding: 0 var(--spacing-4) var(--spacing-6) var(--spacing-4);
      border-bottom: solid var(--stroke-width-l) transparent;
      color: var(--color-text-primary);
      cursor: pointer;
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      transition:
        border-bottom var(--animation-duration) var(--animation-easing),
        color var(--animation-duration) var(--animation-easing);

      .icon {
        padding-right: var(--spacing-4);
        vertical-align: middle;
      }

      .name {
        display: inline-block;
        padding: var(--spacing-4) 0;
        text-transform: capitalize;
      }

      .color-badge {
        margin-left: var(--spacing-4);
        contain-intrinsic-height: none;
      }

      &.selected {
        border-bottom: solid var(--stroke-width-l) var(--color-stroke-background-branding);
        color: var(--color-text-branding);
      }

      &.disabled {
        color: var(--color-text-secondary);
        cursor: not-allowed;
      }
    }
  }
}
</style>
